import Decimal from 'decimal.js';

/**
 * Convert a value to milligrams (mg).
 * Normalizes all units to mg for internal calculations.
 *
 * @param value - The value to convert (number or string)
 * @param fromUnit - The source unit ('mg', 'mcg', or 'IU')
 * @param iuPerMg - Required when fromUnit is 'IU', specifies IU per mg conversion factor
 * @returns Decimal instance representing the value in mg
 * @throws Error if fromUnit is 'IU' and iuPerMg is not provided
 */
export function toMg(
  value: number | string,
  fromUnit: 'mg' | 'mcg' | 'IU',
  iuPerMg?: number
): Decimal {
  const val = new Decimal(value);

  switch (fromUnit) {
    case 'mg':
      return val;
    case 'mcg':
      // 1 mg = 1000 mcg
      return val.dividedBy(1000);
    case 'IU':
      if (iuPerMg === undefined) {
        throw new Error('iuPerMg is required for IU to mg conversion');
      }
      if (iuPerMg <= 0) {
        throw new Error('iuPerMg must be a positive number');
      }
      // Convert IU to mg: IU / (IU per mg)
      return val.dividedBy(iuPerMg);
    default:
      throw new Error(`Unsupported unit: ${fromUnit}`);
  }
}

/**
 * Convert a value to micrograms (mcg).
 * Normalizes all units to mcg for internal calculations.
 *
 * @param value - The value to convert (number or string)
 * @param fromUnit - The source unit ('mg', 'mcg', or 'IU')
 * @param iuPerMg - Required when fromUnit is 'IU', specifies IU per mg conversion factor
 * @returns Decimal instance representing the value in mcg
 * @throws Error if fromUnit is 'IU' and iuPerMg is not provided
 */
export function toMcg(
  value: number | string,
  fromUnit: 'mg' | 'mcg' | 'IU',
  iuPerMg?: number
): Decimal {
  const val = new Decimal(value);

  switch (fromUnit) {
    case 'mg':
      // 1 mg = 1000 mcg
      return val.times(1000);
    case 'mcg':
      return val;
    case 'IU':
      if (iuPerMg === undefined) {
        throw new Error('iuPerMg is required for IU to mcg conversion');
      }
      if (iuPerMg <= 0) {
        throw new Error('iuPerMg must be a positive number');
      }
      // Convert IU to mcg: (IU / (IU per mg)) * 1000
      return val.dividedBy(iuPerMg).times(1000);
    default:
      throw new Error(`Unsupported unit: ${fromUnit}`);
  }
}

/**
 * Convert a value to International Units (IU).
 * Requires iuPerMg conversion factor.
 *
 * @param value - The value to convert (number or string)
 * @param fromUnit - The source unit ('mg' or 'mcg')
 * @param iuPerMg - Required conversion factor: IU per mg
 * @returns Decimal instance representing the value in IU
 * @throws Error if iuPerMg is not provided or invalid
 */
export function toIU(
  value: number | string,
  fromUnit: 'mg' | 'mcg',
  iuPerMg: number
): Decimal {
  if (iuPerMg === undefined || iuPerMg <= 0) {
    throw new Error('iuPerMg must be a positive number');
  }

  const val = new Decimal(value);
  const iuPerMgDecimal = new Decimal(iuPerMg);

  switch (fromUnit) {
    case 'mg':
      // mg * (IU per mg) = IU
      return val.times(iuPerMgDecimal);
    case 'mcg':
      // (mcg / 1000) * (IU per mg) = IU
      return val.dividedBy(1000).times(iuPerMgDecimal);
    default:
      throw new Error(`Unsupported unit for IU conversion: ${fromUnit}`);
  }
}

/**
 * Calculate concentration in mg/mL.
 * Formula: mg/mL = vialMg / reconMl
 *
 * @param vialMg - Total milligrams in the vial (number or string)
 * @param reconMl - Reconstitution volume in milliliters (number or string)
 * @returns Decimal instance representing concentration in mg/mL
 * @throws Error if reconMl is zero or negative
 */
export function mgPerMl(vialMg: number | string, reconMl: number | string): Decimal {
  const vial = new Decimal(vialMg);
  const recon = new Decimal(reconMl);

  if (recon.isZero() || recon.isNegative()) {
    throw new Error('Reconstitution volume must be a positive number');
  }

  return vial.dividedBy(recon);
}

/**
 * Calculate milliliters needed per injection.
 * Formula: mL = desiredMg / mgPerMl
 *
 * @param desiredMg - Desired dose in milligrams (number or string)
 * @param mgPerMl - Concentration in mg/mL (number or string)
 * @returns Decimal instance representing milliliters per injection
 * @throws Error if mgPerMl is zero or negative
 */
export function mlPerInjection(
  desiredMg: number | string,
  mgPerMl: number | string
): Decimal {
  const dose = new Decimal(desiredMg);
  const concentration = new Decimal(mgPerMl);

  if (concentration.isZero() || concentration.isNegative()) {
    throw new Error('Concentration (mg/mL) must be a positive number');
  }

  return dose.dividedBy(concentration);
}

/**
 * Convert milliliters to U-100 insulin units.
 * U-100 syringes have 100 units per milliliter.
 *
 * @param ml - Volume in milliliters (number or string)
 * @returns Decimal instance representing insulin units
 */
export function mlToInsulinUnits(ml: number | string): Decimal {
  const mlDecimal = new Decimal(ml);
  // U-100: 1 mL = 100 units
  return mlDecimal.times(100);
}

/**
 * Convert U-100 insulin units to milliliters.
 *
 * @param units - Insulin units (number or string)
 * @returns Decimal instance representing milliliters
 */
export function insulinUnitsToMl(units: number | string): Decimal {
  const unitsDecimal = new Decimal(units);
  // U-100: 100 units = 1 mL
  return unitsDecimal.dividedBy(100);
}

/**
 * Format a dose value with appropriate precision and unit label.
 *
 * @param value - The dose value (Decimal, number, or string)
 * @param unit - The unit type ('mg', 'mcg', or 'IU')
 * @param options - Optional formatting options
 * @param options.precision - Override default precision (number of decimal places)
 * @returns Formatted string with value and unit (e.g., "250.5 mcg")
 */
export function formatDose(
  value: Decimal | number | string,
  unit: 'mg' | 'mcg' | 'IU',
  options?: { precision?: number }
): string {
  const val = value instanceof Decimal ? value : new Decimal(value);
  let precision: number;

  if (options?.precision !== undefined) {
    precision = options.precision;
  } else {
    // Default precision based on unit type
    switch (unit) {
      case 'mcg':
        // mcg: 0-1 decimal places (e.g., "250" or "250.5")
        precision = val.isInteger() ? 0 : 1;
        break;
      case 'mg':
        // mg: 1-3 decimal places (e.g., "2.5" or "0.001")
        {
          const mgValue = val.toNumber();
          if (mgValue >= 1) {
            precision = 1;
          } else if (mgValue >= 0.01) {
            precision = 2;
          } else {
            precision = 3;
          }
        }
        break;
      case 'IU':
        // IU: 0-2 decimal places
        precision = val.isInteger() ? 0 : val.greaterThanOrEqualTo(100) ? 0 : 2;
        break;
      default:
        precision = 2;
    }
  }

  const formatted = val.toFixed(precision);
  return `${formatted} ${unit}`;
}

/**
 * Format a concentration (mg/mL) with adaptive precision.
 * Automatically switches to mcg/mL for very small values.
 */
export function formatConcentration(value: Decimal | number | string): string {
  const concentration = value instanceof Decimal ? value : new Decimal(value);

  if (!concentration.isFinite()) {
    return `${concentration.toFixed(2)} mg/mL`;
  }

  if (concentration.isZero()) {
    return '0 mg/mL';
  }

  if (concentration.isNegative()) {
    return `${concentration.toFixed(2)} mg/mL`;
  }

  if (concentration.greaterThanOrEqualTo(1)) {
    return `${concentration.toFixed(2)} mg/mL`;
  }

  if (concentration.greaterThanOrEqualTo(0.1)) {
    return `${concentration.toFixed(3)} mg/mL`;
  }

  if (concentration.greaterThanOrEqualTo(0.01)) {
    return `${concentration.toFixed(4)} mg/mL`;
  }

  const mcgPerMl = concentration.times(1000);

  if (mcgPerMl.greaterThanOrEqualTo(100)) {
    return `${mcgPerMl.toFixed(0)} mcg/mL`;
  }

  if (mcgPerMl.greaterThanOrEqualTo(10)) {
    return `${mcgPerMl.toFixed(1)} mcg/mL`;
  }

  return `${mcgPerMl.toFixed(2)} mcg/mL`;
}
